import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';
import { toDasherized } from '../utils/request-helpers.ts';

// =====================================================================
// Helpers
// =====================================================================

/**
 * Creates a base RequestInfo object for a given operation.
 * @param op - The operation type (e.g., 'findAll', 'findRecord').
 * @param id - The record ID (optional).
 * @returns A base RequestInfo object.
 */
function createBaseRequest(
  op: 'findAll' | 'createRecord',
  id?: undefined,
  options?: { skipCache?: boolean }
): RequestInfo;
function createBaseRequest(
  op: 'findRecord' | 'updateRecord' | 'deleteRecord',
  id: string,
  options?: { skipCache?: boolean }
): RequestInfo;
function createBaseRequest(
  _op: RequestInfo['op'],
  id?: string,
  options?: { skipCache?: boolean }
): RequestInfo {
  let url = buildBaseURL({ resourcePath: 'api/v2/reports' });
  if (id) {
    url = `${url}/${id}`;
  }

  const request: RequestInfo = {
    url,
    method: 'GET',
    // These report requests serialize dasherized JSON:API bodies (toDasherized)
    // and rely on EmberData's default dasherizing serializer to read responses.
    // The store's request-manager otherwise defaults `x-json-api-key-format` to
    // `camelized` (jsonApiKeyFormat option, applied via setIfMissing); since
    // tio-api began honoring that header (api/v2 controllers), a camelized
    // server would reject our dasherized attribute keys (report-type,
    // search-config) on save and return camelized keys that break build-mode
    // load. Set dasherized explicitly so request + response round-trip in the
    // format this client is built for (setIfMissing leaves our value intact).
    headers: new Headers({ 'x-json-api-key-format': 'dasherized' }),
  };
  if (options?.skipCache) {
    request.cacheOptions = { [SkipCache]: true };
  }
  return request;
}

/**
 * Builds a query string from a parameters object, converting keys to snake_case.
 * @param params - The parameters for the query string.
 * @returns A URL-encoded query string.
 */
function buildQueryString(params: Record<string, string | undefined>): string {
  const queryParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) {
      const snakeCaseKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
      queryParams.append(`filter[${snakeCaseKey}]`, value);
    }
  }
  return queryParams.toString();
}

/**
 * A JSON:API report resource as returned by the reports endpoint.
 */
export interface ReportResource {
  id: string;
  type?: string;
  attributes?: Record<string, unknown>;
}

/**
 * Map a JSON:API report resource into a plain Report-shaped object.
 *
 * WarpDrive's store.normalize/store.push (the legacy serializer path) is no
 * longer available, so callers read the raw response instead. Attribute keys
 * are read defensively across dasherized / snake / camel casing so the result
 * is correct regardless of the negotiated `x-json-api-key-format`. searchConfig
 * is parsed when the API returns it as a JSON string.
 */
export function reportFromResource(data: ReportResource): Record<string, unknown> {
  const attrs = data?.attributes ?? {};
  const pick = (...keys: string[]): unknown => {
    for (const key of keys) {
      if (attrs[key] !== undefined) return attrs[key];
    }
    return undefined;
  };

  let searchConfig = pick('search-config', 'search_config', 'searchConfig') ?? {};
  if (typeof searchConfig === 'string') {
    try {
      searchConfig = JSON.parse(searchConfig);
    } catch {
      searchConfig = {};
    }
  }

  return {
    id: data.id,
    name: pick('name') ?? '',
    description: pick('description') ?? '',
    reportType: pick('report-type', 'report_type', 'reportType'),
    searchConfig,
    visibilityLevel: pick('visibility-level', 'visibility_level', 'visibilityLevel'),
    partnerAccessConfig:
      pick('partner-access-config', 'partner_access_config', 'partnerAccessConfig') ?? [],
    metadata: pick('metadata') ?? {},
    updatedAt: pick('updated-at', 'updated_at', 'updatedAt'),
  };
}

// =====================================================================
// Public Functions
// =====================================================================

export function queryReportsRequest(params?: {
  reportType?: string;
  visibilityLevel?: string;
  name?: string;
  skipCache?: boolean;
}): RequestInfo {
  const request = createBaseRequest('findAll', undefined, {
    skipCache: params?.skipCache,
  });
  const { skipCache: _, ...filterParams } = params || {};
  const queryString = buildQueryString(filterParams);

  if (queryString) {
    request.url = `${request.url}?${queryString}`;
  }

  return request;
}

export function getReportRequest(id: string): RequestInfo {
  return createBaseRequest('findRecord', id);
}

export function createReportRequest(data: {
  name: string;
  description?: string;
  reportType: string;
  searchConfig: Record<string, unknown>;
  visibilityLevel?: string;
  partnerAccessConfig?: number[];
  metadata?: Record<string, unknown>;
}): RequestInfo {
  const request = createBaseRequest('createRecord');
  request.method = 'POST';
  request.body = JSON.stringify({
    data: {
      type: 'reports',
      attributes: toDasherized(data),
    },
  });
  return request;
}

export function updateReportRequest(
  id: string,
  data: {
    name?: string;
    description?: string;
    reportType?: string;
    searchConfig?: Record<string, unknown>;
    visibilityLevel?: string;
    partnerAccessConfig?: number[];
    metadata?: Record<string, unknown>;
  }
): RequestInfo {
  const request = createBaseRequest('updateRecord', id);
  request.method = 'PATCH';
  request.body = JSON.stringify({
    data: {
      type: 'reports',
      id,
      attributes: toDasherized(data),
    },
  });
  return request;
}

export function deleteReportRequest(id: string): RequestInfo {
  const request = createBaseRequest('deleteRecord', id);
  request.method = 'DELETE';
  return request;
}
